import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  cancelSubscriptionSlots,
  listSubscriptionHolds,
  pauseSubscriptionSlots,
  pauseWebSubscription,
  resumeSubscriptionSlots,
  resumeWebSubscription,
} from '../../api/mealPlansApi';
import { formatDisplayDate } from '../../utils/dateFormat';
import { formatSubscriptionMealSlots, WEEKDAY_COLUMNS } from '../../utils/subscriptionDisplay';
import {
  isApprovedPaidSubscription,
  isPausedSubscription,
  listFutureSlotOccurrences,
  projectToDateAfterPause,
  resolvePurchasedInternalSlotCodes,
  resolveSubscriptionSlotOptions,
  expandCanonicalSlotCodes,
  canonicalizeDeliverySlotCode,
  deliverySlotLabel,
  deliverySlotShortLabel,
  groupSubscriptionHolds,
  todayIsoLocal,
} from './subscriptionActionUtils';
import { useAccountFullscreen } from './AccountLayout';
import './AccountLayout.css';

function selectionKey(dateIso, slotCode) {
  return `${dateIso}::${slotCode}`;
}

function prettySlot(code, fallback) {
  return deliverySlotLabel(code, fallback);
}

function pad2(value) {
  return String(value).padStart(2, '0');
}

function shiftMonth(yearMonth, delta) {
  const [year, month] = String(yearMonth).split('-').map(Number);
  const date = new Date(Date.UTC(year, (month || 1) - 1 + Number(delta || 0), 1));
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}`;
}

function monthLabel(yearMonth) {
  const [year, month] = String(yearMonth).split('-').map(Number);
  return new Date(Date.UTC(year, (month || 1) - 1, 1)).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function buildMonthGrid(yearMonth) {
  const [year, month] = String(yearMonth).split('-').map(Number);
  const firstWeekday = new Date(Date.UTC(year, (month || 1) - 1, 1)).getUTCDay();
  const mondayOffset = (firstWeekday + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month || 1, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < mondayOffset; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(`${year}-${pad2(month)}-${pad2(day)}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * Full-page form for pausing, cancelling, and assigning makeup days.
 */
export default function AccountSubscriptionDeliveriesPanel({
  subscription,
  onBack,
  onUpdated,
}) {
  const [holds, setHolds] = useState([]);
  const [toDate, setToDate] = useState(subscription?.toDate || '');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [slotFilter, setSlotFilter] = useState(null);
  const [pauseMode, setPauseMode] = useState('auto');
  const [makeupDate, setMakeupDate] = useState(() => todayIsoLocal());
  const [resumeDates, setResumeDates] = useState({});
  const [confirmFullPause, setConfirmFullPause] = useState(false);
  const [monthCursor, setMonthCursor] = useState(() => {
    const todayMonth = todayIsoLocal().slice(0, 7);
    const startMonth = String(subscription?.fromDate || todayMonth).slice(0, 7);
    return todayMonth >= startMonth ? todayMonth : startMonth;
  });

  const slotOptions = useMemo(() => resolveSubscriptionSlotOptions(subscription), [subscription]);
  useAccountFullscreen(true);
  const today = todayIsoLocal();
  const minMakeupDate = today;
  const activeSlotFilter = slotFilter ?? slotOptions.map((slot) => slot.code);
  const mealSlotsLabel = formatSubscriptionMealSlots(subscription, subscription?.webPlanSnapshot || {});

  const activeOriginalKeys = useMemo(() => {
    const keys = new Set();
    holds.forEach((hold) => {
      if (hold.status === 'held' || hold.status === 'scheduled') {
        keys.add(selectionKey(
          hold.originalDate,
          canonicalizeDeliverySlotCode(hold.slotCode) || hold.slotCode,
        ));
      }
    });
    return keys;
  }, [holds]);

  const futureSlots = useMemo(
    () => listFutureSlotOccurrences(subscription, {
      today,
      toDate: toDate || subscription?.toDate,
      holds,
    }),
    [subscription, toDate, today, holds],
  );

  const visibleSlots = useMemo(
    () => futureSlots.filter((slot) => activeSlotFilter.includes(slot.slotCode)),
    [activeSlotFilter, futureSlots],
  );

  const groupedDays = useMemo(() => {
    const map = new Map();
    visibleSlots.forEach((slot) => {
      if (!map.has(slot.date)) map.set(slot.date, []);
      map.get(slot.date).push(slot);
    });
    return Array.from(map.entries());
  }, [visibleSlots]);

  const slotsByDate = useMemo(() => new Map(groupedDays), [groupedDays]);
  const monthCells = useMemo(() => buildMonthGrid(monthCursor), [monthCursor]);
  const minMonth = String(subscription?.fromDate || today).slice(0, 7);
  const maxMonth = String(toDate || subscription?.toDate || today).slice(0, 7);

  const pausableSlots = visibleSlots.filter(
    (slot) => !activeOriginalKeys.has(selectionKey(slot.date, slot.slotCode)),
  );
  const pausedVisibleCount = visibleSlots.length - pausableSlots.length;
  const selectedSlots = pausableSlots.filter((slot) => selected.has(slot.id));
  const currentToDate = toDate || subscription?.toDate || '';
  const projectedToDate = projectToDateAfterPause(currentToDate, selectedSlots, slotOptions);
  const willExtend = selectedSlots.length > 0 && pauseMode === 'auto' && projectedToDate > currentToDate;

  const loadHolds = useCallback(async () => {
    if (!subscription?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await listSubscriptionHolds(subscription.id);
      setHolds(Array.isArray(data?.holds) ? data.holds : []);
      if (data?.toDate) setToDate(data.toDate);
    } catch (err) {
      setError(err?.message || 'Unable to load deliveries.');
    } finally {
      setLoading(false);
    }
  }, [subscription?.id]);

  useEffect(() => {
    loadHolds();
  }, [loadHolds]);

  const toggleSlotFilter = (code) => {
    const current = slotFilter ?? slotOptions.map((slot) => slot.code);
    const next = current.includes(code)
      ? current.filter((item) => item !== code)
      : [...current, code];
    setSlotFilter(next.length ? next : slotOptions.map((slot) => slot.code));
  };

  const toggleSelect = (dateIso, slotCode) => {
    const key = selectionKey(dateIso, slotCode);
    if (activeOriginalKeys.has(key)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllForDay = (daySlots) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const unlocked = daySlots.filter(
        (slot) => !activeOriginalKeys.has(selectionKey(slot.date, slot.slotCode)),
      );
      const allOn = unlocked.length > 0 && unlocked.every((slot) => next.has(slot.id));
      unlocked.forEach((slot) => {
        if (allOn) next.delete(slot.id);
        else next.add(slot.id);
      });
      return next;
    });
  };

  const buildItemsPayload = (slots) => {
    const purchased = resolvePurchasedInternalSlotCodes(subscription);
    const byDate = new Map();
    slots.forEach((slot) => {
      const codes = byDate.get(slot.date) || [];
      expandCanonicalSlotCodes(slot.slotCode, purchased).forEach((code) => {
        if (!codes.includes(code)) codes.push(code);
      });
      byDate.set(slot.date, codes);
    });
    return Array.from(byDate.entries()).map(([date, slotCodes]) => ({ date, slotCodes }));
  };

  const refreshParent = async () => {
    if (typeof onUpdated === 'function') await onUpdated();
  };

  const runPauseSelected = async () => {
    if (!selectedSlots.length) {
      setError('Select at least one upcoming meal on the calendar first.');
      return;
    }
    if (pauseMode === 'date' && !makeupDate) {
      setError('Choose the makeup date, or pick another option.');
      return;
    }
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const data = await pauseSubscriptionSlots(subscription.id, {
        items: buildItemsPayload(selectedSlots),
        mode: pauseMode,
        ...(pauseMode === 'date' ? { resumeDate: makeupDate } : {}),
      });
      if (data?.toDate) setToDate(data.toDate);
      setSelected(new Set());
      setSuccess(
        pauseMode === 'open'
          ? 'Meals paused. Assign a makeup day below when you are ready.'
          : 'Meals paused and a makeup day has been scheduled.',
      );
      await loadHolds();
      await refreshParent();
    } catch (err) {
      setError(err?.message || 'Unable to pause selected meals.');
    } finally {
      setBusy(false);
    }
  };

  const runCancelSelected = async () => {
    if (!selectedSlots.length) {
      setError('Select at least one upcoming meal on the calendar first.');
      return;
    }
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const data = await cancelSubscriptionSlots(subscription.id, {
        items: buildItemsPayload(selectedSlots),
      });
      if (data?.toDate) setToDate(data.toDate);
      setSelected(new Set());
      setSuccess('Selected meals cancelled. Makeup days were added at the end of the plan.');
      await loadHolds();
      await refreshParent();
    } catch (err) {
      setError(err?.message || 'Unable to cancel selected meals.');
    } finally {
      setBusy(false);
    }
  };

  const runResumeHold = async (holdIds, fallbackDate) => {
    const ids = (Array.isArray(holdIds) ? holdIds : [holdIds]).filter(Boolean);
    const resumeDate = resumeDates[ids[0]] || fallbackDate;
    if (!resumeDate) {
      setError('Pick a makeup date first.');
      return;
    }
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const data = await resumeSubscriptionSlots(subscription.id, {
        holdIds: ids,
        resumeDate,
      });
      if (data?.toDate) setToDate(data.toDate);
      setResumeDates((current) => {
        const next = { ...current };
        ids.forEach((id) => { delete next[id]; });
        return next;
      });
      const hold = holds.find((row) => ids.includes(row.id));
      setSuccess(
        resumeDate === String(hold?.originalDate || '').slice(0, 10)
          ? 'Meal resumed for the original day.'
          : 'Makeup day assigned.',
      );
      await loadHolds();
      await refreshParent();
    } catch (err) {
      setError(err?.message || 'Unable to assign a makeup day.');
    } finally {
      setBusy(false);
    }
  };

  const runFullPause = async () => {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await pauseWebSubscription(subscription.id);
      setConfirmFullPause(false);
      setSuccess('Entire plan paused.');
      await refreshParent();
    } catch (err) {
      setError(err?.message || 'Unable to pause the plan.');
    } finally {
      setBusy(false);
    }
  };

  const runFullResume = async () => {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await resumeWebSubscription(subscription.id);
      setSuccess('Entire plan resumed. You can manage individual meals again.');
      await refreshParent();
    } catch (err) {
      setError(err?.message || 'Unable to resume the plan.');
    } finally {
      setBusy(false);
    }
  };

  const openHolds = groupSubscriptionHolds(
    holds.filter((hold) => hold.status === 'held' || hold.status === 'scheduled'),
  );
  const openPauses = openHolds.filter((hold) => hold.holdType === 'pause' && hold.status === 'held');
  const canManage = isApprovedPaidSubscription(subscription);
  const pausedPlan = isPausedSubscription(subscription);
  const defaultResume = today;

  return (
    <div className="account-panel account-deliveries">
      <header className="account-deliveries-toolbar">
        <button type="button" className="account-deliveries__back" onClick={onBack} disabled={busy}>
          ← Back to subscriptions
        </button>
        <div className="account-deliveries-toolbar__title">
          <h1>Manage deliveries</h1>
          <p>
            {subscription.subscriptionNumber || `ID ${subscription.id}`}
            {' · '}
            {mealSlotsLabel}
            {' · Ends '}
            {formatDisplayDate(currentToDate, '—')}
          </p>
        </div>
        <div className="account-deliveries-toolbar__stats" aria-label="Delivery summary">
          <span><strong>{visibleSlots.length}</strong> upcoming</span>
          <span><strong>{selectedSlots.length}</strong> selected</span>
          <span><strong>{pausedVisibleCount}</strong> paused</span>
          <span><strong>{openPauses.length}</strong> need makeup</span>
        </div>
      </header>

      {error ? <p className="account-form__error" role="alert">{error}</p> : null}
      {success ? <p className="account-deliveries__success" role="status">{success}</p> : null}

      {pausedPlan ? (
        <section className="account-deliveries__banner">
          <div>
            <h2>This entire plan is paused</h2>
            <p>Individual meals cannot be changed until the plan is resumed.</p>
          </div>
          <button type="button" className="btn-primary" disabled={busy} onClick={runFullResume}>
            {busy ? 'Resuming…' : 'Resume entire plan'}
          </button>
        </section>
      ) : null}

      <div className="account-deliveries-workspace">
        <section className="account-deliveries-cal" aria-label="Delivery calendar">
          <div className="account-deliveries-cal__nav">
            <button
              type="button"
              disabled={busy || monthCursor <= minMonth}
              onClick={() => setMonthCursor((current) => shiftMonth(current, -1))}
              aria-label="Previous month"
            >
              ‹
            </button>
            <h3>{monthLabel(monthCursor)}</h3>
            <button
              type="button"
              disabled={busy || monthCursor >= maxMonth}
              onClick={() => setMonthCursor((current) => shiftMonth(current, 1))}
              aria-label="Next month"
            >
              ›
            </button>
          </div>
          <div className="account-deliveries-cal__weekdays" aria-hidden="true">
            {WEEKDAY_COLUMNS.map((day) => (
              <span key={day.key}>{day.short}</span>
            ))}
          </div>
          {loading ? (
            <p className="account-deliveries-cal__loading">Loading calendar…</p>
          ) : (
            <div className="account-deliveries-cal__grid">
              {monthCells.map((dateIso, index) => {
                if (!dateIso) {
                  return <div key={`empty-${index}`} className="account-deliveries-cal__cell account-deliveries-cal__cell--empty" />;
                }
                const daySlots = slotsByDate.get(dateIso) || [];
                const hasMeals = daySlots.length > 0;
                const isPast = dateIso < today;
                const isToday = dateIso === today;
                const unlocked = daySlots.filter(
                  (slot) => !activeOriginalKeys.has(selectionKey(slot.date, slot.slotCode)),
                );
                const allOn = unlocked.length > 0
                  && unlocked.every((slot) => selected.has(slot.id));
                return (
                  <div
                    key={dateIso}
                    className={[
                      'account-deliveries-cal__cell',
                      isToday ? 'account-deliveries-cal__cell--today' : '',
                      isPast ? 'account-deliveries-cal__cell--past' : '',
                      !hasMeals ? 'account-deliveries-cal__cell--out' : '',
                      hasMeals ? 'account-deliveries-cal__cell--has-meals' : '',
                      !hasMeals ? 'account-deliveries-cal__cell--blank' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <button
                      type="button"
                      className={`account-deliveries-cal__daynum${allOn ? ' is-on' : ''}`}
                      disabled={busy || isPast || !unlocked.length}
                      onClick={() => selectAllForDay(daySlots)}
                      aria-label={`${allOn ? 'Clear' : 'Select'} all meals on ${dateIso}`}
                    >
                      <span className="account-deliveries-cal__agenda-label">
                        {new Date(`${dateIso}T00:00:00Z`).toLocaleDateString('en-IN', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          timeZone: 'UTC',
                        })}
                      </span>
                      <span className="account-deliveries-cal__grid-label">
                        {Number(dateIso.slice(8, 10))}
                      </span>
                    </button>
                    {daySlots.length ? (
                      <div className="account-deliveries-cal__slots">
                        {daySlots.map((slot) => {
                          const key = selectionKey(slot.date, slot.slotCode);
                          const locked = activeOriginalKeys.has(key);
                          const checked = selected.has(key);
                          return (
                            <button
                              key={key}
                              type="button"
                              className={[
                                'account-deliveries-cal__slot',
                                locked ? 'account-deliveries-cal__slot--paused' : '',
                                checked ? 'account-deliveries-cal__slot--selected' : '',
                              ].filter(Boolean).join(' ')}
                              disabled={busy || locked || isPast}
                              aria-pressed={checked}
                              aria-label={`${checked ? 'Deselect' : 'Select'} ${slot.slotLabel || prettySlot(slot.slotCode)} on ${dateIso}`}
                              title={slot.slotLabel || prettySlot(slot.slotCode)}
                              onClick={() => toggleSelect(slot.date, slot.slotCode)}
                            >
                              {slot.shortLabel || deliverySlotShortLabel(slot.slotCode) || slot.slotLabel}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
          <ul className="account-deliveries-cal__legend">
            <li><span className="account-deliveries-cal__slot" /> Meal</li>
            <li><span className="account-deliveries-cal__slot account-deliveries-cal__slot--selected" /> Selected</li>
            <li><span className="account-deliveries-cal__slot account-deliveries-cal__slot--paused" /> Paused</li>
            <li><span className="account-deliveries-cal__dot" /> Today</li>
            <li>Tap a date to select that day’s meals. Pause Breakfast, Lunch, or Dinner separately.</li>
          </ul>
        </section>

        <aside className="account-deliveries-rail">
          {canManage && !pausedPlan ? (
            <>
              <section className="account-deliveries__step">
                <h2>Meal types</h2>
                <div className="account-sub-holds-filters">
                  {slotOptions.map((slot) => {
                    const on = activeSlotFilter.includes(slot.code);
                    return (
                      <button
                        key={slot.code}
                        type="button"
                        className={`account-sub-holds-chip${on ? ' account-sub-holds-chip--on' : ''}`}
                        onClick={() => toggleSlotFilter(slot.code)}
                        disabled={busy}
                      >
                        {slot.label}
                      </button>
                    );
                  })}
                </div>
                <div className="account-deliveries__step-links">
                  <button
                    type="button"
                    className="account-sub-holds-day__all"
                    disabled={busy || !pausableSlots.length}
                    onClick={() => setSelected(new Set(pausableSlots.map((slot) => slot.id)))}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="account-sub-holds-day__all"
                    disabled={busy || selected.size === 0}
                    onClick={() => setSelected(new Set())}
                  >
                    Clear
                  </button>
                </div>
              </section>

              <section className="account-deliveries__step">
                <h2>Pause selected meals</h2>
                {!selectedSlots.length ? (
                  <p className="account-sub-holds-hint">
                    Tap Breakfast, Lunch, or Dinner on the calendar. Each slot can be paused on its own.
                  </p>
                ) : (
                  <>
                    <p>
                      {selectedSlots.length} meal{selectedSlots.length === 1 ? '' : 's'} selected.
                    </p>
                    <div className="account-deliveries-modes">
                      <label className={`account-sub-holds-mode${pauseMode === 'auto' ? ' account-sub-holds-mode--on' : ''}`}>
                        <input
                          type="radio"
                          name="pauseModeForm"
                          checked={pauseMode === 'auto'}
                          onChange={() => setPauseMode('auto')}
                        />
                        <span>
                          <strong>Auto-assign next day</strong>
                          <em>
                            Selected slots stay together and are added at the end of the plan
                            {willExtend
                              ? ` (${formatDisplayDate(currentToDate, currentToDate)} → ${formatDisplayDate(projectedToDate, projectedToDate)})`
                              : ''}
                            .
                          </em>
                        </span>
                      </label>
                      <label className={`account-sub-holds-mode${pauseMode === 'date' ? ' account-sub-holds-mode--on' : ''}`}>
                        <input
                          type="radio"
                          name="pauseModeForm"
                          checked={pauseMode === 'date'}
                          onChange={() => {
                            setPauseMode('date');
                            if (!makeupDate) setMakeupDate(minMakeupDate);
                          }}
                        />
                        <span>
                          <strong>Pick a makeup date</strong>
                          <em>Choose the replacement day now.</em>
                        </span>
                      </label>
                      <label className={`account-sub-holds-mode${pauseMode === 'open' ? ' account-sub-holds-mode--on' : ''}`}>
                        <input
                          type="radio"
                          name="pauseModeForm"
                          checked={pauseMode === 'open'}
                          onChange={() => setPauseMode('open')}
                        />
                        <span>
                          <strong>Decide later</strong>
                          <em>Assign a makeup day from the list below.</em>
                        </span>
                      </label>
                    </div>
                    {pauseMode === 'date' ? (
                      <label className="account-sub-holds-date-field">
                        Makeup date
                        <input
                          type="date"
                          min={minMakeupDate}
                          value={makeupDate}
                          onChange={(e) => setMakeupDate(e.target.value)}
                        />
                      </label>
                    ) : null}
                    <div className="account-deliveries__actions">
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={busy || (pauseMode === 'date' && !makeupDate)}
                        onClick={runPauseSelected}
                      >
                        {busy ? 'Saving…' : `Pause ${selectedSlots.length} meal${selectedSlots.length === 1 ? '' : 's'}`}
                      </button>
                      <button
                        type="button"
                        className="account-sub-card__details-btn"
                        disabled={busy}
                        onClick={runCancelSelected}
                      >
                        Cancel selected
                      </button>
                    </div>
                  </>
                )}
              </section>

              <section className="account-deliveries__step">
                <h2>Current pauses</h2>
                {loading ? (
                  <p>Loading…</p>
                ) : openHolds.length === 0 ? (
                  <p className="account-sub-holds-hint">No paused meals yet.</p>
                ) : (
                  <ul className="account-deliveries-hold-list">
                    {openHolds.map((hold) => {
                      const holdIds = hold.holdIds || [hold.id];
                      const holdKey = holdIds.join('-') || hold.id;
                      const openHold = hold.holdType === 'pause' && hold.status === 'held';
                      const originalDate = String(hold.originalDate || '').slice(0, 10);
                      const canResumeSameDay = openHold && originalDate >= today;
                      const resumeValue = resumeDates[holdIds[0]]
                        || (canResumeSameDay ? originalDate : defaultResume);
                      const isSameDayResume = resumeValue === originalDate;
                      return (
                        <li key={holdKey}>
                          <div>
                            <strong>
                              {formatDisplayDate(hold.originalDate, hold.originalDate)}
                              {' · '}
                              {prettySlot(hold.slotCode, hold.slotLabel)}
                            </strong>
                            <span>
                              {hold.holdType === 'cancel' ? 'Cancelled' : 'Paused'}
                              {hold.resumeDate
                                ? ` · makeup ${formatDisplayDate(hold.resumeDate, hold.resumeDate)}`
                                : ' · makeup not assigned'}
                            </span>
                          </div>
                          {openHold ? (
                            <span className="account-sub-holds-resume-inline">
                              <input
                                type="date"
                                min={minMakeupDate}
                                value={resumeValue}
                                disabled={busy}
                                aria-label={`Makeup date for ${hold.slotLabel || hold.slotCode} on ${hold.originalDate}`}
                                onChange={(e) => setResumeDates((current) => ({
                                  ...current,
                                  [holdIds[0]]: e.target.value,
                                }))}
                              />
                              <button
                                type="button"
                                className="account-sub-card__details-btn"
                                disabled={busy || !resumeValue}
                                onClick={() => runResumeHold(holdIds, resumeValue)}
                              >
                                {isSameDayResume ? 'Resume' : 'Assign'}
                              </button>
                            </span>
                          ) : (
                            <span className="account-sub-holds-scheduled">Scheduled</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </>
          ) : null}
          {!pausedPlan ? (
            <section className="account-deliveries__step account-deliveries__step--muted">
              <h2>Pause entire plan</h2>
              <p>
                Stops every remaining meal. Individual meals cannot be managed while the whole
                plan is paused.
              </p>
              {canManage ? (
                confirmFullPause ? (
                  <div className="account-deliveries__confirm-inline">
                    <p>Pause every remaining meal on this plan?</p>
                    <div className="account-deliveries__actions">
                      <button type="button" className="btn-primary" disabled={busy} onClick={runFullPause}>
                        {busy ? 'Pausing…' : 'Yes, pause entire plan'}
                      </button>
                      <button
                        type="button"
                        className="account-sub-card__details-btn"
                        disabled={busy}
                        onClick={() => setConfirmFullPause(false)}
                      >
                        Keep plan active
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="account-sub-card__details-btn"
                    disabled={busy}
                    onClick={() => setConfirmFullPause(true)}
                  >
                    Pause entire plan
                  </button>
                )
              ) : (
                <p className="account-sub-holds-hint">Full-plan pause is available after approval and payment.</p>
              )}
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
